import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Tooltip} from '@mui/material'
import {makeStyles} from '@mui/styles'
import {Spacing, Colors, Typography} from '../../assets/styles'
import {borderRadiusMedium} from '../../assets/styles/border'
import ToggleButtons from '../ToggleButtons'
import RXRIcon from '../RXRIcon'
import HorizontalBarChart from '../Charts/HorizontalBarChartD3'
import {subReasonPercentages} from './exitSurveyReasonData'

const INFO_COPY =
  'Property-related responses may include multiple categories and sub-reasons per resident, while personal ' +
  'and lifestyle responses are limited to one category and one sub-reason.'

/**
 * @param {Object} props
 * @param {{totalSubmissions: number, categories: Array}} props.breakdown
 * @param {string[]} props.windowLabels
 * @param {number} props.windowIndex
 * @param {(index: number) => void} props.onWindowChange
 */
function ReasonForLeavingHorizontalBarChart({breakdown, windowLabels, windowIndex, onWindowChange}) {
  const classes = useStyles()
  const [selectedCategoryId, setSelectedCategoryId] = useState(null)

  useEffect(() => {
    setSelectedCategoryId(null)
  }, [breakdown])

  const macroData = useMemo(() => (breakdown?.categories || []).map(c => ({id: c.id, label: c.label, value: c.count})), [breakdown])

  const selectedCategory = (breakdown?.categories || []).find(c => c.id === selectedCategoryId) || null
  const subReasonData = useMemo(() => (selectedCategory ? subReasonPercentages(selectedCategory) : []), [selectedCategory])

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader}>
        <h3 className={classes.sectionTitle}>Why Residents Are Leaving</h3>
        <div className={classes.switcher}>
          <ToggleButtons buttonLabels={windowLabels} selected={windowIndex} onChange={onWindowChange} inverseColors />
        </div>
      </div>
      <p className={classes.subCopy}>
        Counts reflect selections, not unique residents.
        <Tooltip title={INFO_COPY} placement="right" arrow>
          <span className={classes.infoIcon}>
            <RXRIcon icon={RXRIcon.INFO} size={16} color={Colors.rxrPrimaryColor} />
          </span>
        </Tooltip>
      </p>
      <div className={classes.totalNote}>{breakdown.totalSubmissions} total submissions</div>

      <HorizontalBarChart
        data={macroData}
        onBarClick={d => setSelectedCategoryId(d.id)}
        selectedId={selectedCategoryId}
        formatValue={v => `${v}`}
      />

      {selectedCategory ? (
        <React.Fragment>
          <hr className={classes.divider} />
          <h3 className={classes.sectionTitle}>{selectedCategory.label} sub-reasons</h3>
          <div className={classes.totalNote}>{selectedCategory.submissions} submissions selected this category</div>
          <HorizontalBarChart data={subReasonData} formatValue={v => `${v}%`} axisMax={100} barColor={Colors.rxrTintColor} />
        </React.Fragment>
      ) : (
        <div className={classes.ctaBanner}>Click a category for reason details</div>
      )}
    </div>
  )
}

const useStyles = makeStyles({
  section: {
    marginBottom: Spacing.spaceMedium,
    padding: Spacing.spaceMedium,
    backgroundColor: Colors.rxrWhiteColor,
    border: `1px solid ${Colors.rxrGreyColor}`,
    borderRadius: borderRadiusMedium,
    boxShadow: Colors.shadowSoft,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Spacing.spaceMedium,
  },
  sectionTitle: {
    fontSize: Typography.fontSizeLarge,
    fontWeight: Typography.heavyFontWeight,
    color: Colors.rxrBlackColor,
    margin: 0,
  },
  infoIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'default',
  },
  switcher: {
    flexShrink: 0,
    width: 160,
  },
  subCopy: {
    display: 'flex',
    alignItems: 'center',
    gap: Spacing.spaceExtraSmall,
    flexWrap: 'wrap',
    fontSize: Typography.fontSizeMediumLarge,
    color: Colors.rxrDarkGreyColor,
    paddingTop: Spacing.spaceSmall,
    paddingBottom: Spacing.spaceExtraSmall,
    margin: 0,
  },
  ctaBanner: {
    marginTop: Spacing.spaceMedium,
    marginLeft: -Spacing.spaceMedium,
    marginRight: -Spacing.spaceMedium,
    marginBottom: -Spacing.spaceMedium,
    paddingTop: Spacing.spaceSmall + 2,
    paddingBottom: Spacing.spaceSmall + 2,
    backgroundColor: Colors.rxrPrimaryColor,
    color: Colors.rxrWhiteColor,
    textAlign: 'center',
    fontSize: Typography.fontSizeMedium,
    borderBottomLeftRadius: borderRadiusMedium,
    borderBottomRightRadius: borderRadiusMedium,
  },
  divider: {
    marginTop: Spacing.spaceMedium,
    marginBottom: Spacing.spaceMedium,
    height: 0,
    border: 'none',
    borderTop: `1px solid ${Colors.rxrLightGreyColor}`,
  },
  totalNote: {
    fontSize: Typography.fontSizeSmall,
    color: Colors.rxrMediumGreyColor,
    paddingBottom: Spacing.spaceMedium,
  },
})

ReasonForLeavingHorizontalBarChart.propTypes = {
  breakdown: PropTypes.shape({
    totalSubmissions: PropTypes.number,
    categories: PropTypes.array,
  }).isRequired,
  windowLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  windowIndex: PropTypes.number.isRequired,
  onWindowChange: PropTypes.func.isRequired,
}

export default ReasonForLeavingHorizontalBarChart
